// =====================================================================
// Post Image provider (Fal.ai) — per-workspace BYOK settings.
// =====================================================================
// Reserved for the upcoming Post Image generator (Beta). The MicroPost
// image-generation pipeline that originally consumed this has been
// retired; the encrypted-key storage and RPC surface remain so users
// who already saved a key don't lose it.
// =====================================================================

import { supabase } from "@/integrations/supabase/client";

export type ImageProvider = "fal";

export type ImageProviderSettings = {
  id: string;
  workspace_id: string;
  provider: ImageProvider;
  api_key_preview: string | null;
  default_model_key: string | null;
  enabled: boolean;
};

export async function getImageProviderSettings(
  workspaceId: string,
  _provider: ImageProvider = "fal",
): Promise<ImageProviderSettings | null> {
  const { data, error } = await supabase
    .from("workspace_image_provider_settings")
    .select("id, workspace_id, api_key_preview, default_model_key, enabled")
    .eq("workspace_id", workspaceId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { ...data, provider: "fal" } as ImageProviderSettings;
}

export type UpsertImageProviderInput = {
  workspaceId: string;
  provider?: ImageProvider;
  apiKey?: string | null; // null/empty = leave existing key untouched
  defaultModelKey?: string | null;
  enabled?: boolean | null;
};

export async function upsertImageProviderSettings(input: UpsertImageProviderInput): Promise<string> {
  const { data, error } = await (supabase.rpc as unknown as (
    fn: "set_workspace_image_provider_key",
    args: Record<string, unknown>,
  ) => Promise<{ data: string | null; error: Error | null }>)(
    "set_workspace_image_provider_key",
    {
      _workspace_id: input.workspaceId,
      _provider: input.provider ?? "fal",
      _api_key: input.apiKey ?? null,
      _default_model_key: input.defaultModelKey ?? null,
      _enabled: input.enabled ?? null,
    },
  );
  if (error) throw error;
  return (data ?? "") as string;
}

/**
 * Curated short-list of Fal.ai models surfaced in Settings. The full list is
 * available at fal.ai/models — extend here as we validate more endpoints.
 */
export const FAL_MODEL_OPTIONS: { value: string; label: string; hint?: string }[] = [
  { value: "fal-ai/flux/schnell", label: "FLUX Schnell", hint: "Fast, low-cost" },
  { value: "fal-ai/flux/dev", label: "FLUX Dev", hint: "Higher quality" },
  { value: "fal-ai/flux-pro/v1.1", label: "FLUX Pro 1.1", hint: "Premium quality" },
  { value: "fal-ai/recraft-v3", label: "Recraft V3", hint: "Strong typography" },
  { value: "fal-ai/ideogram/v2", label: "Ideogram V2", hint: "Text-in-image" },
];
